import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import FeedWall from "@/components/FeedWall";
import FeedComposer from "@/components/FeedComposer";
import TrendingStrip from "@/components/TrendingStrip";
import BasketballChat from "@/components/BasketballChat";
import Messages from "@/components/Messages";
import { FeedGlow } from "@/components/FeedGlow";

export default function Feed() {
    const { user, loading, unreadMessages, markFeedSeen } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const tab = params.get("tab") === "messages" ? "messages" : "posts";

    useEffect(() => {
        if (!loading && !user) navigate("/login?next=/feed", { replace: true });
    }, [user, loading, navigate]);

    // Opening the feed clears the "new posts" portion of the badge.
    useEffect(() => {
        if (user) markFeedSeen();
    }, [user, markFeedSeen]);

    // Keep the sticky section headers pinned right below the (variable-height) site header.
    useEffect(() => {
        const el = document.querySelector('[data-testid="site-header"]');
        if (!el) return;
        const set = () => document.documentElement.style.setProperty("--tsi-sticky-top", `${el.offsetHeight}px`);
        set();
        const ro = new ResizeObserver(set);
        ro.observe(el);
        window.addEventListener("resize", set);
        return () => { ro.disconnect(); window.removeEventListener("resize", set); };
    }, []);

    if (loading || !user) return <div className="text-center text-[#a0a0a0] py-20">Loading…</div>;

    const goTab = (t) => setParams((p) => {
        if (t === "messages") p.set("tab", "messages"); else { p.delete("tab"); p.delete("u"); }
        return p;
    }, { replace: false });

    const tabBtn = (id, label) => {
        const active = tab === id;
        return (
            <button
                type="button"
                data-testid={`feed-tab-${id}`}
                onClick={() => goTab(id)}
                className={`relative font-display text-2xl sm:text-3xl tracking-wide pb-1 transition-colors ${active ? "text-white" : "text-[#5a5a5a] hover:text-[#a0a0a0]"}`}
            >
                {label}
                {id === "messages" && unreadMessages > 0 && (
                    <span data-testid="feed-messages-badge" className="absolute -top-1 -right-4 min-w-[18px] h-[18px] px-1 bg-[#ff4500] text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unreadMessages > 9 ? "9+" : unreadMessages}</span>
                )}
                {active && <span className="absolute left-0 -bottom-0.5 h-0.5 w-full bg-[#ff4500]" />}
            </button>
        );
    };

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-10" data-testid="feed-page">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-3">Your Feed</div>
            <FeedComposer />
            <div className="flex items-center gap-6 mt-6 mb-5 border-b border-[#2a2a2a]">
                {tabBtn("posts", "Posts")}
                {tabBtn("messages", "Messages")}
            </div>
            {tab === "messages" ? (
                <Messages />
            ) : (
                <>
                    <FeedGlow variant="trending"><TrendingStrip /></FeedGlow>
                    <FeedGlow variant="sport" className="mt-6"><BasketballChat /></FeedGlow>
                    <FeedGlow variant="following" className="mt-6"><FeedWall defaultLimit={25} /></FeedGlow>
                </>
            )}
        </div>
    );
}
